import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import NavbarAr from "@/components/NavbarAr";
import Footer from "@/components/Footer";
import FooterAr from "@/components/FooterAr";
import Brand from "@/components/Brand";
import BrandText from "@/components/BrandText";

// Rules & Regulations — bilingual (EN / AR).
//
// APPROVED STATE (EN, do not modify without owner re-authorization):
//   • Header overline: "Conduct"
//   • H1: "Every action reflects CrewZNatioN."
//   • 11 conduct standards organised into three groupings
//     ("On the road" → "At CrewZNatioN gatherings" → "At all times")
//   • Numbered spine preserved (01–11, gold two-digit numerals)
//
// The Arabic version (Feb 2026) mirrors the English meaning without
// weakening, expanding, or removing any community rule. Arabic reads
// naturally in institutional Saudi register — it is not a machine-
// literal translation. Grouping labels, numbering, and the
// continuation-to-Membership link mirror the English structure.

const CONDUCT_GROUPS_EN = [
    {
        label: "On the road",
        items: [
            "Respect Saudi law at all times.",
            "Respect every applicable traffic law.",
            "Drive and ride responsibly. Reckless behaviour is not tolerated.",
            "No burnouts on public or private grounds.",
            "No revving in restricted, residential, or otherwise sensitive areas.",
        ],
    },
    {
        label: "At CrewZNatioN gatherings",
        items: [
            "Respect the organisers, event staff, and security personnel.",
            "Respect the venues that host us.",
            "Entry to any CrewZNatioN event is at the discretion of CrewZNatioN.",
            "Access may be denied or withdrawn at any point, at CrewZNatioN's discretion.",
        ],
    },
    {
        label: "At all times",
        items: [
            "Safety and professionalism are not optional.",
            "Represent CrewZNatioN responsibly, both in the community and in public.",
        ],
    },
];

const CONDUCT_GROUPS_AR = [
    {
        label: "على الطريق",
        items: [
            "احترم أنظمة المملكة العربية السعودية في جميع الأوقات.",
            "احترم جميع أنظمة وأحكام المرور المعمول بها.",
            "قد مركبتك بمسؤولية. لا مكان للتهوّر في مجتمع CrewZNatioN.",
            "يُمنع منعًا باتًا تفحيط الإطارات في أي مكان، عامًّا كان أم خاصًّا.",
            "يُمنع رفع صوت المحرّك (Revving) في المناطق المُقيّدة أو السكنية أو أي أماكن حسّاسة.",
        ],
    },
    {
        label: "في فعاليات CrewZNatioN",
        items: [
            "احترم المنظّمين، وطاقم العمل، ورجال الأمن.",
            "احترم الأماكن التي تستضيفنا.",
            "الدخول إلى أي فعالية من فعاليات CrewZNatioN يخضع لتقدير CrewZNatioN.",
            "يجوز لـ CrewZNatioN منع الوصول أو سحبه في أي وقتٍ ودون إبداء أسباب.",
        ],
    },
    {
        label: "في كل الأوقات",
        items: [
            "السلامة والاحتراف ليسا خيارًا.",
            "مثّل CrewZNatioN بشرفٍ ومسؤولية، داخل المجتمع وفي الفضاء العام على حدٍّ سواء.",
        ],
    },
];

const COPY_EN = {
    overline: "Conduct",
    title: <>Every action reflects <Brand />.</>,
    lead: (
        <>
            Every action reflects <Brand />. These principles exist so that every member, guest and participant represents the institution with responsibility, respect and professionalism. Together, they define the standard expected whenever someone acts in its company.
        </>
    ),
    closing: (
        <>
            By being present at a <Brand /> event, every person acknowledges these standards and agrees to hold them.
        </>
    ),
    continuationPrefix: "These standards are what membership asks in practice. ",
    continuationLinkLabel: "Apply for membership →",
    switchLabel: "العربية",
    metaTitle: "Rules & Regulations — CrewZNatioN",
    metaDesc:
        "The conduct standards CrewZNatioN holds every member, guest and participant to — on the road, at gatherings, and at all times.",
    canonical: "https://www.crewznation.com/rules",
};

const COPY_AR = {
    overline: "السلوك",
    title: <>كل تصرّفٍ يعكس <Brand />.</>,
    lead: (
        <>
            كل تصرّفٍ يعكس <Brand />. وُضِعت هذه المبادئ ليمثّل كل عضوٍ وضيفٍ ومشاركٍ المؤسسة بروح المسؤولية والاحترام والاحتراف. وهي مجتمعةً تُحدِّد المستوى المتوقَّع من كل من يقف تحت هذه المظلّة.
        </>
    ),
    closing: (
        <>
            بمجرّد حضور أي فعاليةٍ من فعاليات <Brand />، يُعدّ الحاضر مُقرًّا بهذه المبادئ ومُلتزمًا بها.
        </>
    ),
    continuationPrefix: "هذه المبادئ هي ما تطلبه العضوية عمليًّا. ",
    continuationLinkLabel: "قدِّم طلب العضوية →",
    switchLabel: "English",
    metaTitle: "الأنظمة واللوائح — CrewZNatioN",
    metaDesc:
        "مبادئ السلوك التي تلتزم بها CrewZNatioN مع كل عضوٍ وضيفٍ ومشارك — على الطريق، وفي الفعاليات، وفي كل الأوقات.",
    canonical: "https://www.crewznation.com/ar/rules",
};

export default function Rules({ locale = "en" }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const Foot = isAr ? FooterAr : Footer;
    const t = isAr ? COPY_AR : COPY_EN;
    const groups = isAr ? CONDUCT_GROUPS_AR : CONDUCT_GROUPS_EN;
    const other = isAr ? "/rules" : "/ar/rules";

    // Continuous numbering across the three groupings (01 → 11).
    let counter = 0;

    // Per-locale <title>, meta description and canonical.
    useEffect(() => {
        const prevTitle = document.title;
        document.title = t.metaTitle;

        let m = document.querySelector('meta[name="description"]');
        const prevDesc = m ? m.getAttribute("content") : null;
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "description");
            document.head.appendChild(m);
        }
        m.setAttribute("content", t.metaDesc);

        let c = document.querySelector('link[rel="canonical"]');
        const prevCanonical = c ? c.getAttribute("href") : null;
        if (!c) {
            c = document.createElement("link");
            c.setAttribute("rel", "canonical");
            document.head.appendChild(c);
        }
        c.setAttribute("href", t.canonical);

        return () => {
            document.title = prevTitle;
            if (m && prevDesc !== null) m.setAttribute("content", prevDesc);
            if (c) {
                if (prevCanonical !== null) c.setAttribute("href", prevCanonical);
                else c.parentNode?.removeChild(c);
            }
        };
    }, [t.metaTitle, t.metaDesc, t.canonical]);

    return (
        <div
            className={`min-h-screen bg-[var(--crewz-bg)] ${isAr ? "font-arabic" : ""}`}
            dir={isAr ? "rtl" : "ltr"}
            data-testid={isAr ? "rules-page-ar" : "rules-page"}
        >
            <Nav />

            {/* HEADER */}
            <section className="px-6 md:px-12 lg:px-20 pt-24 pb-12 max-w-5xl mx-auto">
                <div className={`flex items-center gap-3 mb-5 ${isAr ? "justify-end" : ""}`}>
                    <span className="gold-rule" />
                    <p className={`overline ${isAr ? "font-arabic !text-[22.04px] md:!text-[23.8px]" : ""}`}>
                        {t.overline}
                    </p>
                </div>
                <h1
                    className={`font-display ${isAr ? "text-4xl md:text-5xl !leading-[1.25]" : "text-5xl md:text-6xl leading-[0.98]"} text-[var(--crewz-text)] max-w-3xl`}
                    data-testid="rules-title"
                >
                    {t.title}
                </h1>
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-8 max-w-2xl`}
                >
                    {t.lead}
                </p>

                {/* Language switch */}
                <div className={`mt-8 ${isAr ? "text-right" : "text-left"}`}>
                    <Link
                        to={other}
                        dir={isAr ? "ltr" : "rtl"}
                        className="inline-flex items-center gap-2 border border-[var(--crewz-gold)] text-[var(--crewz-gold)] hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] px-5 py-2 text-[12px] uppercase tracking-[0.22em] font-bold transition-colors duration-300"
                        data-testid="rules-lang-switch"
                        aria-label={isAr ? "Switch to English" : "التبديل إلى العربية"}
                    >
                        {t.switchLabel}
                    </Link>
                </div>
            </section>

            {/* BODY — three groupings, continuous numbered spine */}
            <section className="px-6 md:px-12 lg:px-20 pb-16 max-w-4xl mx-auto" data-testid="rules-body">
                <div className="space-y-16">
                    {groups.map((group, gi) => (
                        <div key={group.label} data-testid={`rules-group-${gi + 1}`}>
                            <p className={`overline mb-6 text-zinc-500 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}>
                                {isAr ? group.label : <BrandText>{group.label}</BrandText>}
                            </p>
                            <div className="border-t border-white/10">
                                {group.items.map((text) => {
                                    counter += 1;
                                    return (
                                        <div
                                            key={counter}
                                            className={`flex items-start gap-6 py-6 border-b border-white/10`}
                                            data-testid={`rule-${counter}`}
                                        >
                                            <span
                                                className="font-display text-3xl text-[var(--crewz-gold)] w-12 shrink-0"
                                                dir="ltr"
                                            >
                                                {String(counter).padStart(2, "0")}
                                            </span>
                                            <p
                                                className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-base md:text-lg font-light leading-relaxed"} text-[var(--crewz-text)]/90 pt-1`}
                                            >
                                                {isAr ? text : <BrandText>{text}</BrandText>}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Closing institutional sentence */}
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-base md:text-lg font-light leading-relaxed"} text-[var(--crewz-text)]/85 mt-16 max-w-2xl`}
                >
                    {t.closing}
                </p>
            </section>

            {/* CONTINUATION TO MEMBERSHIP */}
            <section
                className="px-6 md:px-12 lg:px-20 pt-8 pb-32 max-w-4xl mx-auto"
                data-testid="rules-continuation"
            >
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-base md:text-lg font-light leading-relaxed"} text-[var(--crewz-text)]/70 max-w-2xl`}
                >
                    {t.continuationPrefix}
                    <Link
                        to={isAr ? "/ar/membership" : "/membership"}
                        className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline"
                        data-testid="rules-apply-link"
                    >
                        {t.continuationLinkLabel}
                    </Link>
                </p>
            </section>

            <Foot />
        </div>
    );
}
